import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { featureAccess } from "../permissions/access";
import { useUserRole } from "../utils/hooks/useUserRole";
import { useDebounce } from "../utils/hooks/useDebounce";
import { useBranches } from "../components/SpecialOrder/useBranches";
import NoteListFilter, {
  EMPTY_NOTE_LIST_FILTER,
  NoteListFilterValue,
  noteFilterParams,
} from "../components/CreditDebitNote/Filter";
import NoteTable from "../components/CreditDebitNote/Table";
import NoteExportButton from "../components/CreditDebitNote/NoteExportButton";
import {
  CREDIT_DEBIT_NOTE_MENU_TITLE,
  CREDIT_DEBIT_NOTE_NEW_PATH,
} from "../components/CreditDebitNote/constants";

const SEARCH_DEBOUNCE_MS = 500;

export default function CreditDebitNoteListPage() {
  const navigate = useNavigate();
  const role = useUserRole();
  const canSelectBranch = featureAccess.creditDebitNoteAllBranches(role);
  const canExport = featureAccess.creditDebitNoteExport(role);
  const branches = useBranches(canSelectBranch);

  const [filter, setFilter] = useState<NoteListFilterValue>(
    EMPTY_NOTE_LIST_FILTER,
  );
  const debouncedSearch = useDebounce(filter.search, SEARCH_DEBOUNCE_MS);
  const { branchCode, noteType, dateFrom, dateTo, status, settlementMethod, reasonType } =
    filter;
  // Rebuilt only when a filter (or the settled search) changes, so typing does not refetch.
  const filterParams = useMemo(
    () =>
      noteFilterParams({
        branchCode,
        noteType,
        dateFrom,
        dateTo,
        status,
        settlementMethod,
        reasonType,
        search: debouncedSearch,
      }),
    [branchCode, noteType, dateFrom, dateTo, status, settlementMethod, reasonType, debouncedSearch],
  );

  const handleFilterChange = (patch: Partial<NoteListFilterValue>) =>
    setFilter((previous) => ({ ...previous, ...patch }));
  const handleCreate = () => navigate(CREDIT_DEBIT_NOTE_NEW_PATH);

  return (
    <Stack sx={{ padding: "8px 0" }} gap={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        flexWrap="wrap"
      >
        <Typography variant="h6" fontWeight={700}>
          {CREDIT_DEBIT_NOTE_MENU_TITLE}
        </Typography>
        <Stack direction="row" gap={1} alignItems="flex-start" flexWrap="wrap">
          {canExport && <NoteExportButton params={filterParams} />}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            ออกเอกสารใหม่
          </Button>
        </Stack>
      </Stack>
      <Paper sx={{ padding: "24px 16px" }}>
        <NoteListFilter
          canSelectBranch={canSelectBranch}
          branches={branches}
          value={filter}
          onChange={handleFilterChange}
        />
      </Paper>
      <NoteTable params={filterParams} />
    </Stack>
  );
}
